// components/stock/StockMovements.tsx
import { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { createResource,getResources,getResourceById, updateResource} from '../../../router/api';

interface StockMovement {
  id: string;
  date_mouvement: string;
  type_mouvement: 'ENTREE' | 'SORTIE' | 'AJUSTEMENT_POS' | 'AJUSTEMENT_NEG' | 'TRANSFERT_ENTREE' | 'TRANSFERT_SORTIE' | 'INVENTAIRE';
  quantite: number;
  prix_unitaire?: number;
  document_source: string;
  document_id?: string | number;
  motif?: string;
  utilisateur?: { name: string };
  produit: {
    reference: string;
    short_name: string;
  };
  emplacement_source?: { code: string };
  emplacement_destination?: { code: string };
}

export default function StockMovements() {
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all'); // maintenant string
  const [dateFilter, setDateFilter] = useState('all');

  useEffect(() => {
    const fetchMovements = async () => {
      try {
        setLoading(true);
        const response = await getResources('mouvements-stock');
        setMovements(response.data.data || response.data);
      } catch (error) {
        console.error('Erreur chargement mouvements:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMovements();
  }, []);

  // Filtrage corrigé
  const filteredMovements = movements.filter(mvt => {
    const matchesSearch =
      mvt.produit.reference.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mvt.produit.short_name.toLowerCase().includes(searchTerm.toLowerCase());

    // CORRECTION ICI : comparaison avec les vraies valeurs
    const matchesType = typeFilter === 'all' || mvt.type_mouvement === typeFilter;

    const today = new Date().toISOString().split('T')[0];
    const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
    const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];

    const matchesDate =
      dateFilter === 'all' ||
      (dateFilter === 'today' && mvt.date_mouvement.startsWith(today)) ||
      (dateFilter === 'yesterday' && mvt.date_mouvement.startsWith(yesterday)) ||
      (dateFilter === 'week' && mvt.date_mouvement >= weekAgo);

    return matchesSearch && matchesType && matchesDate;
  });

  const totalEntries = movements.filter(m => m.quantite > 0).reduce((sum, m) => sum + m.quantite, 0);
  const totalExits = Math.abs(movements.filter(m => m.quantite < 0).reduce((sum, m) => sum + m.quantite, 0));

  const getTypeLabel = (type: string) => {
    const map: Record<string, string> = {
      ENTREE: 'Entrée en stock',
      SORTIE: 'Sortie',
      AJUSTEMENT_POS: 'Ajustement +',
      AJUSTEMENT_NEG: 'Ajustement -',
      TRANSFERT_ENTREE: 'Transfert (Entrée)',
      TRANSFERT_SORTIE: 'Transfert (Sortie)',
      INVENTAIRE: 'Inventaire'
    };
    return map[type] || type;
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      ENTREE: 'bg-green-100 text-green-800',
      SORTIE: 'bg-blue-100 text-blue-800',
      AJUSTEMENT_POS: 'bg-orange-100 text-orange-800',
      AJUSTEMENT_NEG: 'bg-red-100 text-red-800',
      TRANSFERT_ENTREE: 'bg-purple-100 text-purple-800',
      TRANSFERT_SORTIE: 'bg-purple-100 text-purple-800',
      INVENTAIRE: 'bg-gray-200 text-gray-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getTypeIcon = (type: string) => {
    const icons: Record<string, string> = {
      ENTREE: 'ri-inbox-line',
      SORTIE: 'ri-shopping-cart-line',
      AJUSTEMENT_POS: 'ri-add-line',
      AJUSTEMENT_NEG: 'ri-subtract-line',
      TRANSFERT_ENTREE: 'ri-arrow-left-right-line',
      TRANSFERT_SORTIE: 'ri-arrow-left-right-line',
      INVENTAIRE: 'ri-clipboard-line'
    };
    return icons[type] || 'ri-file-line';
  };

  // Export Excel (inchangé, fonctionne parfaitement)
  const exportToExcel = () => {
    const dataToExport = filteredMovements.map(mvt => {
      const date = new Date(mvt.date_mouvement);
      return {
        'Date': date.toLocaleDateString('fr-FR'),
        'Heure': date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
        'Type': getTypeLabel(mvt.type_mouvement),
        'Référence': mvt.produit.reference,
        'Produit': mvt.produit.short_name,
        'Quantité': mvt.quantite > 0 ? `+${mvt.quantite}` : mvt.quantite,
        'Document': `${mvt.document_source}${mvt.document_id ? ' #' + mvt.document_id : ''}`,
        'Emplacement': mvt.emplacement_source?.code && mvt.emplacement_destination?.code
          ? `${mvt.emplacement_source.code} → ${mvt.emplacement_destination.code}`
          : (mvt.emplacement_source?.code || mvt.emplacement_destination?.code || ''),
        'Utilisateur': mvt.utilisateur?.name || 'Système',
        'Motif': mvt.motif || '',
        'Prix unitaire HT': mvt.prix_unitaire ? parseFloat(mvt.prix_unitaire.toString()).toFixed(4) + ' GNF' : ''
      };
    });

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const colWidths = Object.keys(dataToExport[0] || {}).map((key) => ({
      wch: Math.min(
        Math.max(key.length, ...dataToExport.map(row => (row as any)[key]?.toString()?.length || 0)) + 4,
        50
      )
    }));
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Mouvements Stock");
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
    const fileName = `mouvements_stock_${new Date().toISOString().slice(0,10)}.xlsx`;
    saveAs(data, fileName);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="animate-spin w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full mb-4"></div>
        <p className="text-gray-600 text-lg">Chargement des mouvements...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cartes de stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Total Entrées</p>
              <p className="text-3xl font-bold mt-2">{totalEntries}</p>
              <p className="text-green-100 text-sm mt-1">unités reçues</p>
            </div>
            <i className="ri-arrow-down-line text-3xl opacity-80"></i>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Total Sorties</p>
              <p className="text-3xl font-bold mt-2">{totalExits}</p>
              <p className="text-blue-100 text-sm mt-1">unités sorties</p>
            </div>
            <i className="ri-arrow-up-line text-3xl opacity-80"></i>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">Mouvements</p>
              <p className="text-3xl font-bold mt-2">{movements.length}</p>
              <p className="text-purple-100 text-sm mt-1">opérations</p>
            </div>
            <i className="ri-arrow-left-right-line text-3xl opacity-80"></i>
          </div>
        </div>
      </div>

      {/* Filtres + Export + Tableau */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
            <input
              type="text"
              placeholder="Rechercher produit, SKU, document..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* FILTRE PAR TYPE CORRIGÉ */}
          <select 
            value={typeFilter} 
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 pr-8 cursor-pointer"
          >
            <option value="all">Tous les types</option>
            <option value="ENTREE">Entrée en stock</option>
            <option value="SORTIE">Sortie</option>
            <option value="AJUSTEMENT_POS">Ajustement (+)</option>
            <option value="AJUSTEMENT_NEG">Ajustement (-)</option>
            <option value="TRANSFERT_ENTREE">Transfert (entrée)</option>
            <option value="TRANSFERT_SORTIE">Transfert (sortie)</option>
            <option value="INVENTAIRE">Inventaire</option>
          </select>

          <select 
            value={dateFilter} 
            onChange={(e) => setDateFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 pr-8 cursor-pointer"
          >
            <option value="all">Toutes les dates</option>
            <option value="today">Aujourd'hui</option>
            <option value="yesterday">Hier</option>
            <option value="week">7 derniers jours</option>
          </select>

          <button
            onClick={exportToExcel}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all flex items-center gap-3 font-medium shadow-md hover:shadow-lg"
          >
            <i className="ri-file-excel-line text-xl"></i>
            Exporter Excel
            <span className="text-xs opacity-80">({filteredMovements.length} lignes)</span>
          </button>
        </div>

        {/* Le reste du tableau reste IDENTIQUE */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th className="text-left py-4 px-4 font-semibold text-gray-700">Date & Heure</th>
                <th className="text-left py-4 px-4 font-semibold text-gray-700">Type</th>
                <th className="text-left py-4 px-4 font-semibold text-gray-700">Produit</th>
                <th className="text-center py-4 px-4 font-semibold text-gray-700">Qté</th>
                <th className="text-left py-4 px-4 font-semibold text-gray-700">Document</th>
                <th className="text-left py-4 px-4 font-semibold text-gray-700">Emplacement</th>
                <th className="text-left py-4 px-4 font-semibold text-gray-700">Utilisateur</th>
                <th className="text-left py-4 px-4 font-semibold text-gray-700">Notes</th>
              </tr>
            </thead>
            <tbody>
              {filteredMovements.map((mvt) => {
                const date = new Date(mvt.date_mouvement);
                return (
                  <tr key={mvt.id} className="border-b hover:bg-gray-50">
                    <td className="py-4 px-4">
                      <div>
                        <p className="font-medium">{date.toLocaleDateString('fr-FR')}</p>
                        <p className="text-sm text-gray-500">{date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 w-fit ${getTypeColor(mvt.type_mouvement)}`}>
                        <i className={getTypeIcon(mvt.type_mouvement)}></i>
                        {getTypeLabel(mvt.type_mouvement)}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <div>
                        <p className="font-medium">{mvt.produit.short_name}</p>
                        <p className="text-sm text-gray-500 font-mono">{mvt.produit.reference}</p>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span className={`font-bold text-lg ${mvt.quantite > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {mvt.quantite > 0 ? '+' : ''}{mvt.quantite}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded font-mono text-sm">
                        {mvt.document_source} #{mvt.document_id || '-'}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-600">
                      {mvt.emplacement_source?.code && mvt.emplacement_destination?.code
                        ? `${mvt.emplacement_source.code} → ${mvt.emplacement_destination.code}`
                        : mvt.emplacement_source?.code || mvt.emplacement_destination?.code || '-'}
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-xs font-bold">
                          {mvt.utilisateur?.name?.[0] || 'S'}
                        </div>
                        <span>{mvt.utilisateur?.name || 'Système'}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-600 max-w-xs">
                      {mvt.motif || '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredMovements.length === 0 && (
          <div className="text-center py-12">
            <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
            <p className="text-gray-500 text-lg">Aucun mouvement trouvé</p>
          </div>
        )}
      </div>
    </div>
  );
}